import {
    FinanceCreatorPaymentOutstanding,
    getOutstandingCreatorPayments,
    GetOutstandingCreatorPaymentsParams,
} from '@round/api';
import { useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { showNotification } from '../../../../utils/showNotification';

type Params = Omit<GetOutstandingCreatorPaymentsParams, 'page'>;

export function useOutstandingPayments({
    page_size,
    payment_due_start_date,
    payment_due_end_date,
    booking_id,
    influencer_id,
    manager_id,
}: Params) {
    const [payments, setPayments] = useState<FinanceCreatorPaymentOutstanding[]>([]);
    const [totals, setTotals] = useState<Record<number, number> | null>(null);
    const [page, setPage] = useState(1);
    const [isInitialized, setIsInitialized] = useState(false);
    const [hasError, setHasError] = useState<boolean>(false);
    const [count, setCount] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);

    const fetchData = async (params: GetOutstandingCreatorPaymentsParams, requestInit?: RequestInit) => {
        try {
            setHasError(false);

            const response = await getOutstandingCreatorPayments(params, requestInit);

            const {
                data: { results, count, next },
            } = response;

            setPayments((prev) => prev.concat(results));
            setTotals(response.data.payment_amount_total);
            setCount(count);
            setHasNextPage(!!next);

            setIsInitialized(true);
        } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') {
                return;
            }

            setHasError(true);
            setIsInitialized(true);
            throw error;
        }
    };

    const init = async (requestInit: RequestInit) => {
        await fetchData(
            {
                page,
                page_size,
                payment_due_start_date,
                payment_due_end_date,
                booking_id,
                influencer_id,
                manager_id,
            },
            requestInit
        );
    };

    const reset = () => {
        setPayments([]);
        setTotals(null);
        setPage(1);
        setCount(0);
        setHasNextPage(false);
        setIsInitialized(false);
    };

    const loadNextPage = useMemo(
        () =>
            debounce(
                async (requestInit?: RequestInit) => {
                    await fetchData(
                        {
                            page: page + 1,
                            page_size,
                            payment_due_start_date,
                            payment_due_end_date,
                            booking_id,
                            influencer_id,
                            manager_id,
                        },
                        requestInit
                    );
                    setPage(page + 1);
                },
                500,
                { leading: true, trailing: false }
            ),
        [page, page_size, payment_due_start_date, payment_due_end_date, booking_id, influencer_id, manager_id]
    );

    const refreshPayment = async (id: number) => {
        const response = await getOutstandingCreatorPayments({ booking_id: id });
        const refreshedPayment = response.data.results[0];

        if (response.status !== 200 || !refreshedPayment) {
            showNotification('could not refresh payment', 'error');
            return;
        }

        setPayments((prev) =>
            prev.map((payment) => {
                if (payment.id === id) {
                    return refreshedPayment;
                }
                return payment;
            })
        );

        const refreshedTotalResponse = await getOutstandingCreatorPayments({
            page_size: 0,
            payment_due_start_date,
            payment_due_end_date,
            booking_id,
            influencer_id,
            manager_id,
        });

        if (refreshedTotalResponse.status !== 200) {
            showNotification('could not refresh totals', 'error');
            return;
        }
        setTotals(refreshedTotalResponse.data.payment_amount_total);
    };

    return {
        payments,
        totals,
        count,
        hasNextPage,
        isInitialized,
        isLoading: !isInitialized && !hasError,
        hasError,
        init,
        reset,
        loadNextPage,
        refreshPayment,
    };
}
